/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type QnaMakerContextualTreeItemProps = {
    isCurrentQna: boolean;
    isRoot: boolean;
    level: number;
    displayText: string;
    onClick?: () => void;
};

const scopedTheme = getScopedTheme('QnaMakerContextualTreeItem');

const { getClassName, styleDiv } = getStylistV2('QnaMakerContextualTreeItem');
const ChildIcon = styleDiv('childIconDiv', {
    width: 10,
    height: 10,
    borderLeft: `1px solid ${scopedTheme.secondaryColor}`,
    borderBottom: `1px solid ${scopedTheme.secondaryColor}`
});
const ChildIconWrapper = styleDiv('ChildIconWrapper', { width: 10, height: 18 });

export const QnaMakerContextualTreeItem = observer((props: QnaMakerContextualTreeItemProps) => {
    const { isRoot, isCurrentQna, displayText, level, onClick = noop } = props;

    const { rootStore } = useGlobalState();
    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const ROOT_INDENT_WIDTH = 14;
    const CHILD_INDENT_WIDTH = 20;
    const EXTRA_WIDTH = isRoot ? 40 : 13;

    const indentWidth = (level + 1) * (isRoot ? ROOT_INDENT_WIDTH : CHILD_INDENT_WIDTH);
    const maxWidth = `calc(100% - ${indentWidth}px - ${EXTRA_WIDTH}px)`;

    const isChildQna = level > 0 && !isCurrentQna;
    const truncatedTextClassName = getClassName('truncatedTextClassName', {
        maxWidth,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'black',
        fontStyle: isChildQna ? 'italic' : '',
        $nest: { '&:hover': { color: isCurrentQna ? 'black' : scopedTheme.secondaryColor, cursor: isCurrentQna ? '' : 'pointer' } }
    });

    const renderChildIcon = (): JSX.Element =>
        !isRoot && (
            <ChildIconWrapper>
                <ChildIcon />
            </ChildIconWrapper>
        );

    return (
        <Stack horizontal verticalAlign="center" tokens={{ padding: `0 0 0 ${indentWidth}px`, childrenGap: 3 }}>
            {renderChildIcon()}

            <LuisText
                as="a"
                onClick={onClick}
                tabIndex={0}
                title={displayText}
                dir={languageDirection}
                className={truncatedTextClassName}
                onKeyUp={e => e.key === LuisKeyCodes.Enter && onClick()}
                fontWeight={isCurrentQna && level === 0 ? 600 : 'initial'}>
                {displayText}
            </LuisText>
        </Stack>
    );
});
